import type { TranslationKey } from "./de.js";

export const en: Record<TranslationKey, string> = {
  // ── StartScreen ──────────────────────────────────────────
  "start.title": "SOLITAIRE",
  "start.setOfDay": "Set of the Day",
  "start.setOfDay.sub": "Classic Klondike — one set after another",
  "start.journey": "The Eternal Journey",
  "start.journey.sub": "Play through the sets — one by one",
  "start.draw1": "Draw 1",
  "start.weekly": "Weekly",
  "start.weekly.sub": "Weekly league — coming soon",
  "start.tower": "Tower of Babel",
  "start.tower.sub": "Endless Solitaire — coming soon",
  "start.klondike": "Klondike",
  "start.klondike.sub": "Draw 3 cards",
  "start.soon": "Soon",

  // ── GamePage ─────────────────────────────────────────────
  "game.settings": "Settings",
  "game.logout": "Sign out",
  "game.graphicSettings": "Graphic settings",
  "game.login": "Sign in",
  "game.pts": "pts",
  "game.undoLimitReached": "Undo limit reached ({{limit}}×)",
  "game.undoCount": "Undo ({{used}}/{{limit}})",
  "game.undoMove": "Undo move",
  "game.retryTitle": "Replay this set (without scoring)",
  "game.retry": "Retry",
  "game.nextSetTitle": "Next set",
  "game.nextSet": "Next ›",
  "game.endTitle": "End set and show results",
  "game.end": "End",
  "game.leaderboardTitle": "Leaderboard for this set",
  "game.homeTitle": "Back to start",
  "game.helpTitle": "Help for this game mode",
  "game.helpClose": "Close",
  "game.helpDaily": "Set of the Day\n\nEvery day there is a new set for all players to solve. Compare your score on the leaderboard!\n\nTap a card to select it, then tap the target to move it. Or drag & drop cards onto matching piles.\n\nGoal: Place all 52 cards sorted by suit (Ace → King) onto the four foundation piles.",
  "game.helpJourney": "The Eternal Journey\n\nPlay through the sets step by step — starting from Set 1. Your progress is saved.\n\nTap a card to select it, then tap the target to move it. Or drag & drop cards onto matching piles.\n\nGoal: Place all 52 cards sorted by suit (Ace → King) onto the four foundation piles.",
  "game.helpJourneyKlondike": "The Eternal Journey — Klondike (Draw 3)\n\nPlay through the sets step by step. 3 cards are drawn from the stock — only the top card is playable.\n\nTap a card or drag & drop it onto matching piles.\n\nGoal: Place all 52 cards sorted by suit (Ace → King) onto the four foundation piles.",
  "game.helpKlondike": "Klondike (Draw 3)\n\nEvery day there is a new set for all players. 3 cards are drawn from the stock — only the top card is playable.\n\nTap a card or drag & drop it onto matching piles.\n\nGoal: Place all 52 cards sorted by suit (Ace → King) onto the four foundation piles.",
  "game.helpDefault": "Solitaire (Klondike)\n\nTap a card to select it, then tap the target to move it. Or drag & drop cards onto matching piles.\n\nIn the tableau, stack cards in alternating red/black in descending order.\n\nGoal: Place all 52 cards sorted by suit (Ace → King) onto the four foundation piles.",
  "game.moves": "moves",

  // ── Ende-Overlay ─────────────────────────────────────────
  "end.title": "Set finished",
  "end.timeBonusReduced": "Time bonus (÷{{divisor}})",
  "end.foundation": "Foundation ({{count}} cards)",
  "end.flips": "Flips ({{count}}×)",
  "end.rotations": "Rotations ({{count}}× extra)",
  "end.shakes": "Shakes ({{count}}×)",
  "end.total": "Total",
  "end.saving": "Saving…",
  "end.rank": "🏆 Rank {{rank}}",
  "end.saved": "Saved",
  "end.showLeaderboard": "Show leaderboard",
  "end.nextSet": "Next set ›",
  "end.retry": "Retry",

  // ── Gewinn-Overlay ───────────────────────────────────────
  "win.title": "You won!",
  "win.timeBonus": "Time bonus ({{seconds}}s)",
  "win.foundation52": "Foundation (52 cards)",
  "win.winBonus": "Win bonus",

  // ── LoginPage ────────────────────────────────────────────
  "auth.email": "Email",
  "auth.emailPlaceholder": "your@email.com",
  "auth.password": "Password",
  "auth.passwordPlaceholder": "min. 6 characters",
  "auth.passwordLoginPlaceholder": "Your password",
  "auth.loading": "Please wait…",
  "auth.register": "Register",
  "auth.loginBtn": "Sign in",
  "auth.or": "or",
  "auth.googleSoon": "Coming soon",
  "auth.googleLogin": "Sign in with Google",
  "auth.googleComingSoon": "(coming soon)",
  "auth.loginTab": "Sign in",
  "auth.registerTab": "Register",
  "auth.playWithout": "Play without account →",

  // ── Auth-Fehler (useAuthStore) ───────────────────────────
  "auth.error.usernameInvalid": "Username: max. 10 characters, letters and numbers only.",
  "auth.error.loginFailed": "Email or password incorrect.",
  "auth.error.notLoggedIn": "Not logged in.",
  "auth.error.wrongPassword": "Current password is incorrect.",

  // ── UsernameField ────────────────────────────────────────
  "username.label": "Username",
  "username.placeholder": "e.g. max99",
  "username.idle": "max. 10 characters, letters & numbers only",
  "username.checking": "Checking…",
  "username.available": "✓ Available",
  "username.taken": "✗ Already taken",
  "username.invalid": "✗ Letters/numbers only, max. 10 characters",

  // ── SettingsPage ─────────────────────────────────────────
  "settings.back": "← Back",
  "settings.title": "Settings",
  "settings.account": "Account",
  "settings.username": "Username",
  "settings.changeEmail": "Change email",
  "settings.currentEmail": "(current: {{email}})",
  "settings.newEmail": "New email address",
  "settings.change": "Change",
  "settings.emailSent": "✓ Confirmation email sent — please check your inbox.",
  "settings.changePassword": "Change password",
  "settings.currentPassword": "Current password",
  "settings.newPassword": "New password",
  "settings.passwordChanged": "✓ Password changed successfully.",
  "settings.loginToManage": "to manage account settings.",
  "settings.graphics": "Graphics",
  "settings.sessionOnly": "Settings apply to this session only.",
  "settings.loginToSave": "to save them permanently.",
  "settings.cardSet": "Card set",
  "settings.cardBack": "Card back",
  "settings.tableColor": "Table color",
  "settings.saving": "Saving…",
  "settings.saveGraphics": "Save graphics",
  "settings.graphicsSaved": "✓ Saved",
  "settings.leftHanded": "Left-handed mode",
  "settings.leftHandedDesc": "Stock/waste on right, foundation on left",
  "settings.autoAce": "Auto Ace",
  "settings.autoAceDesc": "Visible aces automatically fly to the foundation",
  "settings.animationSpeed": "Animation speed",

  // ── Set-Picker ─────────────────────────────────────────────
  "game.pickSetTitle": "Choose Set",
  "game.pickSetNext": "Next Set ({{next}})",
  "game.pickSetOr": "or enter set number",
  "game.pickSetRange": "Set 1 – {{max}}",
  "game.pickSetGo": "Go",
  "game.pickSetPlaceholder": "Set no.",
  "settings.speedSlow": "Slow",
  "settings.speedNormal": "Normal",
  "settings.speedFast": "Fast",

  // ── LeaderboardPage ──────────────────────────────────────
  "lb.noGame": "No game specified.",
  "lb.back": "← Back",
  "lb.title": "LEADERBOARD",
  "lb.gameNumber": "Game #{{seed}}",
  "lb.play": "Play",
  "lb.loading": "Loading leaderboard…",
  "game.shuffling": "Shuffling cards…",
  "lb.noEntries": "No entries yet",
  "lb.beFirst": "Be the first to win this game!",
  "lb.rank": "#",
  "lb.username": "Username",
  "lb.score": "Score",
  "lb.time": "Time",
  "lb.movesHeader": "Moves",
  "lb.you": "(you)",
  "lb.error": "Failed to load",
  "lb.retry": "Try again",
  "lb.footer": "Top {{count}} · sorted by score",
  "lb.yourRank": "Your rank",
  "lb.rankOfTotal": "Rank {{rank}} of {{total}}",

  // ── Tischfarben (config.ts) ──────────────────────────────
  "bg.casinoGreen": "Casino Green",
  "bg.filzGreen": "Dark Green",
  "bg.waldGreen": "Forest Green",
  "bg.pokerFelt": "Poker Felt",
  "bg.electricPurple": "Electric Purple",
  "bg.rebeccaPurple": "RebeccaPurple",

  // ── ProfilePage (Statistics) ─────────────────────────────
  "profile.back": "← Back",
  "profile.title": "Statistics",
  "profile.daily": "Set of the Day",
  "profile.journey": "The Eternal Journey",
  "profile.weekly": "Weekly",
  "profile.tower": "Tower of Babel",
  "profile.klondike": "Klondike",
  "profile.comingSoon": "Coming soon",
  "profile.noData": "No games this month",
  "profile.won": "Won",
  "profile.lost": "Not completed",
  "profile.notPlayed": "Not played",

  // ── LogoutPage ──────────────────────────────────────────────
  "logout.title": "Thanks for visiting!",
  "logout.subtitle": "You have been signed out.",
  "logout.continue": "Continue playing",

  // ── Kartenset-Namen (CardSetContext) ──────────────────────
  "cardset.classic3": "Classic 3 (Large)",
  "cardset.classic4": "Classic 4 (Generated)",
  "cardset.classic5": "Classic 5 (No Mirror)",
};
